import {TargetBlank} from '@wandb/common/util/links';
import React, {useMemo} from 'react';
import {QueryResult} from 'react-apollo';
import {Link} from 'react-router-dom';

import {
  Exact,
  FetchArtifactCollectionTriggersQuery,
  FetchProjectTriggersQuery,
  useArtifactCollectionQuery,
  useFetchProjectTriggersQuery,
} from '../../../generated/graphql';
import {propagateErrorsContext} from '../../../util/errors';
import {queue as queueUrl} from '../../../util/urls';
import {job as jobUrl} from '../../../util/urls';
import {Tooltip} from '../../Tooltip';
import {Table} from '../Table';
import {EventTypeChip} from './EventTypeChip';
import {TriggerActions} from './TriggerActions';
import * as S from './TriggersTab.styles';
type TriggerTableProps = {
  projectTriggersQuery: QueryResult<
    FetchProjectTriggersQuery,
    Exact<{
      entityName: string;
      projectName: string;
    }>
  >;
  artifactCollectionTriggersQuery?: QueryResult<FetchArtifactCollectionTriggersQuery>;
  actionTypes?: TriggeredActionType[];
  columnWidth?: number;
};

interface TriggersTabProps {
  projectName: string;
  entityName: string;
  projectID: string;
  isNewUI: boolean;
}

export type TriggeredActionType =
  | 'QueueJobTriggeredAction'
  | 'NotificationTriggeredAction';

const RE_VERSION = /^v(\d+)$/;

export const TriggersTab = ({entityName, projectName}: TriggersTabProps) => {
  const triggersQuery = useFetchProjectTriggersQuery({
    variables: {projectName, entityName},
  });
  return (
    <TriggerTable projectTriggersQuery={triggersQuery} columnWidth={500} />
  );
};

export const TriggerTable = ({
  projectTriggersQuery,
  artifactCollectionTriggersQuery,
  actionTypes,
  columnWidth,
}: TriggerTableProps) => {
  const rows = useMemo(() => {
    let projectScopedTriggers =
      projectTriggersQuery?.data?.project?.triggers ?? [];
    if (artifactCollectionTriggersQuery) {
      projectScopedTriggers = projectScopedTriggers.filter(
        t => t.scope.__typename === 'Project'
      );
    }
    const acScopedTriggers =
      artifactCollectionTriggersQuery?.data?.artifactCollection?.triggers.edges.map(
        t => {
          return t?.node;
        }
      ) ?? [];
    let triggersData = [...acScopedTriggers, ...projectScopedTriggers];
    if (actionTypes) {
      triggersData = triggersData?.filter(t =>
        actionTypes.includes(t.triggeredAction?.__typename)
      );
    }
    return (
      triggersData
        ?.map(trigger => {
          // Derive job information
          const fullJobName =
            trigger.triggeredAction?.__typename === 'QueueJobTriggeredAction'
              ? JSON.parse(trigger.triggeredAction.template).job
              : '';
          const [entityName, projectName, jobNameWithAlias] =
            fullJobName?.split('/') ?? [];
          const [jobName, alias] = jobNameWithAlias?.split(':') ?? [];

          // Derive queue information
          const queue =
            trigger.triggeredAction?.__typename === 'QueueJobTriggeredAction' &&
            trigger.triggeredAction?.queue
              ? {
                  Name: trigger.triggeredAction.queue!.name,
                  ID: trigger.triggeredAction.queue!.id,
                }
              : {};
          return {
            row: {
              id: trigger.id,
              name: (
                <S.Cell>
                  <S.Name>{trigger.name}</S.Name>
                  {trigger.description}
                </S.Cell>
              ),
              triggerEventType: (
                <S.Cell>
                  <EventTypeChip
                    eventType={trigger.triggeringCondition.eventType}
                  />
                </S.Cell>
              ),
              job: jobName ? (
                <TriggerJobLink
                  entityName={entityName}
                  projectName={projectName}
                  jobName={jobName}
                  alias={alias}
                />
              ) : (
                <S.Cell>{''}</S.Cell>
              ),
              queue: queue?.ID !== undefined && (
                <S.Cell>
                  <TargetBlank href={queueUrl(entityName, queue.ID, 'runs')}>
                    {queue.Name}
                  </TargetBlank>
                </S.Cell>
              ),
              deleteTrigger: (
                //  Override general cell alignment properties for actions' overflow menu
                <S.Cell style={{alignItems: 'flex-end'}}>
                  <TriggerActions
                    triggerID={trigger.id}
                    triggerName={trigger.name}
                    refetchProjectTriggers={projectTriggersQuery.refetch}
                    refetchCollectionTriggers={
                      artifactCollectionTriggersQuery?.refetch
                    }
                  />
                </S.Cell>
              ),
            },
            searchString: trigger.name,
          };
        })
        .filter(r => r.row.id != null) || []
    );
  }, [projectTriggersQuery, artifactCollectionTriggersQuery, actionTypes]);
  const columns = [
    {
      Header: 'Automation name',
      accessor: 'name',
      resizable: true,
      width: columnWidth ?? 300,
    },
    {
      Header: 'Trigger type',
      accessor: 'triggerEventType',
    },
    {
      Header: 'Job',
      accessor: 'job',
      width: columnWidth ?? 300,
    },
    {
      Header: 'Queue',
      accessor: 'queue',
    },
    {
      Header: '',
      accessor: 'deleteTrigger',
      width: 200,
    },
  ];
  const acTriggersLoading = artifactCollectionTriggersQuery
    ? artifactCollectionTriggersQuery.data == null
    : false;
  return (
    <Table
      data={rows}
      loading={projectTriggersQuery?.data == null || acTriggersLoading}
      columns={columns}
      pageSize={20}
      defaultSorted={[{id: 'name', desc: true}]}
    />
  );
};

type TriggerJobLinkProps = {
  entityName: string;
  projectName: string;
  jobName: string;
  alias: string;
};

export const TriggerJobLink = ({
  entityName,
  projectName,
  jobName,
  alias,
}: TriggerJobLinkProps) => {
  const jobArtifactQuery = useArtifactCollectionQuery({
    variables: {
      artifactCollectionName: jobName,
      artifactTypeName: 'job',
      entityName,
      projectName,
    },
    context: propagateErrorsContext(),
  });
  const jobArtifactData =
    jobArtifactQuery?.data?.project?.artifactType?.artifactCollection;
  const jobId = jobArtifactData?.id;
  let jobVersionNumber;
  let jobVersion;
  const match = alias.match(RE_VERSION);
  if (!match) {
    // alias is not a version hence iterate through the list of aliases
    // to find the version number
    const aliases = jobArtifactData?.aliases.edges;
    jobVersionNumber = aliases?.find(edge => edge.node?.alias === alias)?.node
      ?.artifact?.versionIndex;
    jobVersion = `v${jobVersionNumber}`;
  } else {
    jobVersion = alias;
  }

  if (jobArtifactQuery.error?.graphQLErrors) {
    console.warn('Invalid job name for trigger - job has been deleted');
    return (
      <S.Cell>
        <Tooltip
          content={`"${jobName}:${alias}" was deleted so this trigger can no longer run. We recommend that you delete it.`}
          position="bottom left"
          trigger={<S.ErrorHighlight>Deleted</S.ErrorHighlight>}
        />
      </S.Cell>
    );
  }

  if (
    jobArtifactQuery.loading ||
    !jobArtifactQuery.data ||
    !jobId ||
    !jobVersion
  ) {
    return <S.Cell>{''}</S.Cell>;
  }

  // job url
  const jobParams = {
    entityName,
    projectName,
    jobId,
    jobVersion,
    tab: 'runs',
  };
  const url = jobUrl(jobParams);

  return (
    <S.Cell>
      <S.LinkedName>
        <Link to={url}>{`${jobName}:${alias}`}</Link>
      </S.LinkedName>
    </S.Cell>
  );
};
